import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import { deacService } from '../../../services/central-api/deac';
import Toast from 'react-native-toast-message';
import { getApplicationsStatus, useVacancyStatus } from './VacanciesCommons';
import moment from 'moment';
import DeleteApplicationModal from './DeleteApplicationModal';
import { useAuth } from '../../../context/Auth';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

interface Props {
    vacancyId: number;
    setIsCancellingApplications: Dispatch<SetStateAction<boolean>>;
}

export default function VacancyApplications({ vacancyId, setIsCancellingApplications }: Props) {
    const { user: authenticatedUser } = useAuth();

    const inViewMode = !(authenticatedUser?.permissions.deac_manage_vacancies ||
        authenticatedUser?.permissions.deac_manage_acting_body_units_vacancies ||
        authenticatedUser?.permissions.deac_manage_all_acting_bodies_vacancies
    );
    const [filters, setFilters] = useFilters<{ textFilter: string | undefined; page: number; limit: number; }>({
        textFilter: undefined,
        page: 0,
        limit: 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [vacancyApplications, setVacancyApplications] = useState<VacancyApplication[]>([]);
    const [selectedVacancyApplications, setSelectedVacancyApplications] = useState<VacancyApplication[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);
    const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
    const [allowForSelectableRows, setAllowForSelectableRows] = useState<boolean>(false);
    const [cancelApplicationModal, setCancelApplicationModal] = useState<boolean>(false);
    const [currentAction, setCurrentAction] = useState<'none' | 'cancelApplication'>('none');

    async function getVacancyApplications(): Promise<void> {
        try {
            setIsLoading(true);
            const response = await deacService.getVacancyApplications(vacancyId, filters);
            setVacancyApplications(response.rows);
            setTotalItems(response.count);

            if (response.rows.length > 0) {
                const isOpened = moment().isBetween(response.rows[0].vacancy.applicationsStart, response.rows[0].vacancy.activityStart, null, '[)');
                setAllowForSelectableRows(response.rows.some(application => {
                    return (application.deletedAt === null && isOpened);
                }));
            }
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            setClearSelectedRows(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getVacancyApplications();
        }
    }, [isFocused, filters]));

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Card style={{ padding: 0 }}>
            <DataTableHeader
                title={translate('agentsApplications')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
                actions={!inViewMode && <>
                    {currentAction === 'none'
                        ? <CustomButton
                            text={translate('cancelApplication')}
                            onPress={() => {
                                setClearSelectedRows(false);
                                setCurrentAction('cancelApplication');
                                setIsCancellingApplications(true);
                            }}
                            disabled={!allowForSelectableRows}
                        />
                        : <View style={{ flexDirection: 'row', gap: 10 }}>
                            <CustomButton
                                text={translate('cancelSelection')}
                                type='secondary'
                                onPress={() => {
                                    setClearSelectedRows(true);
                                    setSelectedVacancyApplications([]);
                                    setAllRowsSelected(false);
                                    setCurrentAction('none');
                                    setIsCancellingApplications(false);
                                }}
                            />

                            <CustomButton
                                text={translate('concludeSelection')}
                                type='primary'
                                disabled={selectedVacancyApplications.length === 0 && !allRowsSelected}
                                onPress={() => {
                                    setCancelApplicationModal(true);
                                }}
                            />
                        </View>}
                </>
                }
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    clearSelectedRows={clearSelectedRows}
                    selectableRows={!inViewMode && currentAction != 'none'}
                    selectableRowDisabled={(row) => {
                        const vacancyStatus = useVacancyStatus(row.vacancy);
                        return (row.deletedAt !== null ||
                                vacancyStatus.isCanceled ||
                                vacancyStatus.isConfirmed ||
                                vacancyStatus.isProgress ||
                                vacancyStatus.isFinished
                        );
                    }}
                    onSelectedRowsChange={(selectedRows) => {
                        setSelectedVacancyApplications(selectedRows.selectedRows);
                        setAllRowsSelected(selectedRows.allSelected);
                    }}
                    columns={[
                        {
                            name: translate('status'),
                            cell: row => getApplicationsStatus(row),
                            grow: 0,
                        },
                        {
                            name: translate('agentName'),
                            cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                            grow: 1,
                        },
                        {
                            name: 'RF',
                            cell: row => row.user.registry,
                            grow: 1,
                        },
                        {
                            name: translate('role'),
                            cell: row => row.user.role.name,
                            grow: 1,
                        },
                        {
                            name: translate('activityTitle'),
                            cell: row => row.vacancy.title,
                            grow: 1,
                        },
                        {
                            name: translate('appliedIn'),
                            cell: row => moment(row.createdAt).format('DD/MM/YYYY'),
                            grow: 1,
                        },
                        {
                            name: translate('agentActingBody'),
                            cell: row => row.user.actingBodyUnit.actingBody.name,
                            grow: 1,
                        },
                        {
                            name: translate('agentUnit'),
                            cell: row => row.user.actingBodyUnit.name,
                            grow: 1,
                        },
                        {
                            name: translate('canceledIn'),
                            cell: row => row.deletedAt && moment(row.deletedAt).format('DD/MM/YYYY'),
                            grow: 1,
                        },
                        {
                            name: translate('canceledBy'),
                            cell: row => row.deletedBy?.warName && row.deletedBy.warName != '' ? row.deletedBy.warName : row.deletedBy?.name,
                            grow: 1,
                        },
                    ]}
                    data={vacancyApplications}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                    }}
                />}
            />
            <DeleteApplicationModal
                isModalVisible={cancelApplicationModal}
                setModalVisible={setCancelApplicationModal}
                vacancyId={vacancyId}
                onConfirm={() => {
                    setClearSelectedRows(true);
                    setCancelApplicationModal(false);
                    setSelectedVacancyApplications([]);
                    setAllRowsSelected(false);
                    getVacancyApplications();
                    setCurrentAction('none');
                    setIsCancellingApplications(false);
                }}
                applicationsIds={allRowsSelected ? undefined : selectedVacancyApplications.map(a => a.id)}
            />
        </Card>
    );
}
